<template>
  <v-navigation-drawer
    class="panel"
    v-model="drawerControl"
    width="400"
    disable-route-watcher
    app
    floating
  >
    <!-- Prepend -->
    <NavigationDrawerPrepend
      @click="drawerControl = !drawerControl"
      slot="prepend"
    >
      <div slot="buttons">
        <slot name="buttons" />
      </div>
    </NavigationDrawerPrepend>

    <!-- Meio -->
    <div
      class="panel-content"
      :class="{
        'panel-content-mirror': isHomeViewModeMirror,
      }"
    >
      <ProgrammedActivityList v-if="isHomeViewModeMirror" />

      <!-- TODO: Criar HomeNavigationDrawerFilters -->
      <v-expansion-panels v-else flat accordion>
        <v-expansion-panel>
          <v-expansion-panel-header ripple>
            <v-icon>folder_open</v-icon>
            <div class="title">
              {{ 'Navegar por gavetas' }}
              <div class="subtitle">Escolha uma ou mais gavetas</div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-text-field
              v-if="showInputSearchFolder"
              v-model="filter.path"
              class="ma-2 input-search-folder"
              placeholder="Pesquisar gaveta..."
              outlined
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              icon
              dense
              :loading="loadingFolders"
              @input="$emit('update-filter', filter)"
            />
            <template v-if="!systemFoldersLength">
              <div class="feedback-message">
                Gavetas não encontradas
              </div>
            </template>
            <template v-else>
              <v-treeview
                :class="
                  homeViewMode !== viewModes.TABLE
                    ? 'highlight-selected-nodes'
                    : ''
                "
                v-model="selectedFolders"
                label="Gavetas"
                :items="systemFolders"
                selected-color="primary"
                dense
                open-on-click
                :open.sync="openNodes"
                :active.sync="activeState"
                :selectable="homeViewMode !== viewModes.TABLE"
                :activatable="homeViewMode === viewModes.TABLE"
                hoverable
              />
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel
          v-if="$vuetify.breakpoint.mdAndDown"
          :disabled="
            ![viewModes.CARD, viewModes.CONTACT].includes(homeViewMode)
          "
        >
          <v-expansion-panel-header
            ripple
            :class="{
              disabled: ![viewModes.CARD, viewModes.CONTACT].includes(
                homeViewMode,
              ),
            }"
          >
            <v-icon>filter_list</v-icon>
            <div class="title">
              {{ 'Refinar Resultados' }}
              <div class="subtitle">Selecione um ou mais filtros</div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="item">
              <v-menu
                ref="menuFromDate"
                v-model="menuFromDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="fromDateFormatted"
                    slot="activator"
                    @click:clear="clearFromDate()"
                    v-on="on"
                    label="Data Inicial"
                    placeholder="Escolha uma data inicial..."
                    color="graydark"
                    outlined
                    hide-details
                    clearable
                    autocomplete="new-password"
                    prepend-inner-icon="today"
                    append-icon="mdi-menu-down"
                    readonly
                  ></v-text-field>
                </template>

                <v-date-picker
                  v-model="fromDate"
                  show-current
                  no-title
                  scrollable
                  color="primary"
                  locale="pt-br"
                  :max="maxFromDateValue"
                  @click:date="validateDates()"
                  @input="menuFromDate = false"
                >
                </v-date-picker>
              </v-menu>
            </div>

            <div class="item">
              <v-menu
                ref="menuToDate"
                v-model="menuToDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="toDateFormatted"
                    slot="activator"
                    @click:clear="clearToDate()"
                    v-on="on"
                    label="Data Final"
                    placeholder="Escolha uma data final..."
                    color="graydark"
                    outlined
                    hide-details
                    clearable
                    autocomplete="new-password"
                    prepend-inner-icon="event"
                    append-icon="mdi-menu-down"
                    readonly
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="toDate"
                  show-current
                  no-title
                  scrollable
                  color="primary"
                  locale="pt-br"
                  :min="minToDateValue"
                  :max="maxToDateValue"
                  @click:date="validateDates()"
                  @input="menuToDate = false"
                >
                </v-date-picker>
              </v-menu>
            </div>

            <div class="item">
              <v-text-field
                v-model="author"
                @click:clear="author = undefined"
                label="Autor"
                placeholder="Adicione o autor..."
                color="graydark"
                outlined
                hide-details
                clearable
                autocomplete="new-password"
                prepend-inner-icon="person"
              ></v-text-field>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Footer -->
    <HomeNavigationDrawerFooter
      slot="append"
      @click="clearFilters"
      :homeViewMode="homeViewMode"
      :hasActiveFilters="hasActiveFilters"
    />
  </v-navigation-drawer>
</template>

<script>
import _ from 'lodash';
import { dateFormat, isoDateFormat } from '@/utils/date.js';
import { viewModes } from '@/utils/mappers';
import { getInitiallyOpenNodes } from '@/utils/treeView';
import HomeNavigationDrawerFooter from '@/components/home/HomeNavigationDrawer/HomeNavigationDrawerFooter.vue';
import NavigationDrawerPrepend from '@/components/home/HomeNavigationDrawer/HomeNavigationDrawerPrepend.vue';
import ProgrammedActivityList from '@/components/mirrorPreview/ProgrammedActivityList.vue';

export default {
  name: 'HomeNavigationDrawer',
  components: {
    HomeNavigationDrawerFooter,
    NavigationDrawerPrepend,
    ProgrammedActivityList,
  },
  props: {
    drawer: Boolean,
    showInputSearchFolder: Boolean,
    systemFolders: undefined,
    loadingFolders: Boolean,
    homeViewMode: undefined,
    tableFolder: Array,
  },

  data() {
    return {
      author: undefined,
      filter: { path: '' },
      fromDate: undefined,
      toDate: undefined,
      selectedFolders: [],
      activatedFolder: [],
      openFolders: [],
      places: undefined,
      place: undefined,
      menuFromDate: null,
      menuToDate: null,
      maxFromDateValue: undefined,
      maxToDateValue: undefined,
      minToDateValue: undefined,
      viewModes,
      openNodes: [],
    };
  },
  beforeRouteUpdate(to, from, next) {
    this.fromDate = to.query.fromDate || '';
    this.toDate = to.query.toDate || '';
    this.author = to.query.author || '';
    this.folders = to.query.folders || '';
    next();
  },
  created() {
    if (this.tableFolder && this.tableFolder.length > 0) {
      this.activatedFolder = this.tableFolder;
    }
  },
  computed: {
    systemFoldersLength() {
      return this.systemFolders && this.systemFolders.length;
    },
    drawerControl: {
      get() {
        return this.drawer;
      },
      set(value) {
        if (this.drawer !== value) this.toggleFilters();
      },
    },
    activeState: {
      get() {
        return this.homeViewMode === viewModes.TABLE
          ? this.activatedFolder
          : [];
      },
      set(value) {
        if (value.length > 0) {
          this.activatedFolder = value;
        }
      },
    },
    hasActiveFilters() {
      return (
        !!this.fromDate ||
        !!this.toDate ||
        !!this.author ||
        this.selectedFolders.length > 0
      );
    },
    filters() {
      return {
        author: this.author,
        fromDate: this.fromDate,
        toDate: this.toDate,
        cardFolders: this.selectedFolders,
        tableFolder: this.activatedFolder,
      };
    },
    selectedPlaces() {
      let selectedPlaces = [];
      this.selectedFolders.forEach(folder => {
        const currentPlace = folder.split('.')[0];
        if (selectedPlaces.indexOf(currentPlace) === -1) {
          selectedPlaces.push(currentPlace);
        }
      });
      return selectedPlaces;
    },
    fromDateFormatted: {
      get() {
        return this.fromDate ? dateFormat(this.fromDate) : undefined;
      },
      set() {},
    },
    toDateFormatted: {
      get() {
        return this.toDate ? dateFormat(this.toDate) : undefined;
      },
      set() {},
    },
    maxDateValue() {
      return isoDateFormat(new Date());
    },
    debouncableStates() {
      return [
        this.selectedFolders,
        this.menuFromDate,
        this.menuToDate,
        this.fromDate,
        this.toDate,
        this.author,
      ].join();
    },
    isHomeViewModeMirror() {
      return this.homeViewMode === this.viewModes.MIRROR;
    },
  },
  watch: {
    systemFolders() {
      if (this.systemFolders && this.systemFolders.length > 0) {
        // Calling update filters again when folder list response is received
        // to avoid having non selected treeview after the filters are selected
        // and the page is reloaded, this component seems to reset the v-model somewhere when loading
        this.updateActiveFilters();

        this.openNodes = getInitiallyOpenNodes(
          this.homeViewMode === viewModes.TABLE
            ? this.tableFolder
            : this.selectedFolders,
        );
      }
    },
    debouncableStates() {
      this.$emit('update-filters', this.filters);
      this.applyFilters();
    },
    activatedFolder() {
      if (this.activatedFolder[0] !== this.tableFolder[0]) {
        this.$emit('get-folder-content', this.activatedFolder);
      }
    },
  },
  methods: {
    updateActiveFilters() {
      this.selectedFolders = this.$route.query.folders
        ? this.selectedFolders.concat(this.$route.query.folders)
        : [];
      this.activatedFolder = [this.$route.query.folder];
      this.fromDate = this.$route.query.fromDate;
      this.toDate = this.$route.query.toDate;
      this.author = this.$route.query.author;
      this.maxFromDateValue = this.maxDateValue;
      this.maxToDateValue = this.maxDateValue;
      this.$emit('has-active-filters', this.hasActiveFilters);
    },
    validateDates() {
      // 1. make sure fromDate is always smaller or equal toDate
      // 2. make sure toDate is not bigger than today
      this.minToDateValue = this.fromDate ? this.fromDate : undefined;
      this.maxFromDateValue = this.toDate ? this.toDate : this.maxDateValue;
    },
    clearFilters() {
      this.fromDate = undefined;
      this.toDate = undefined;
      this.author = undefined;
      this.selectedFolders = [];
      this.minToDateValue = undefined;
      this.maxFromDateValue = this.maxToDateValue;
    },
    toggleFilters() {
      this.$emit('toggle-filters', this.filters);
      this.$emit('has-active-filters', this.hasActiveFilters);
    },
    clearFromDate() {
      this.fromDate = undefined;
      this.validateDates();
    },
    clearToDate() {
      this.toDate = undefined;
      this.validateDates();
    },
    applyFilters: _.debounce(function() {
      if (!(this.menuFromDate || this.menuToDate)) {
        this.$emit('apply-filters');
        this.$emit('has-active-filters', this.hasActiveFilters);
      }
    }, parseInt(process.env.VUE_APP_FILTER_DELAY) || 2000),
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/navigationDrawer-v2.scss';
@import '@/assets/scss/inputs.scss';
@import '@/assets/scss/buttons.scss';
@import '@/assets/scss/theme-v2.scss';

.input-search-folder {
  ::v-deep {
    .v-input__prepend-inner {
      margin-top: auto !important;
      margin-bottom: auto !important;
    }
  }
  border: 1px solid rgba(171, 169, 169, 0.307);
}

.feedback-message {
  flex: 0 0 72%;
  margin: 16px 0 0 0;
  font-size: 0.88em;
  font-style: italic;
  color: $color-black-56;
  line-height: 1.2em;
  text-align: center;

  span {
    font-family: 'globotipo' !important;
    font-weight: 600 !important;
  }

  @include for-size(phone-only) {
    flex: 0 0 80%;
    font-size: 0.88em;
    margin: 8px 0 0 0;
  }
}
.panel-content-mirror {
  height: 100%;
}
.exibition-mode,
.content-mode {
  display: none;
}

@media screen and (max-width: 840px) {
  .exibition-mode,
  .content-mode {
    display: block;
  }
}

.highlight-selected-nodes {
  ::v-deep {
    .v-treeview-node {
      &.v-treeview-node--selected {
        background-color: rgba($color-primary, 0.04);
      }
    }
  }
}
</style>
